import React from 'react';
import { Box, ListItem, Typography } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Explore, LocationOnRounded } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { unpackToTime } from '@campfire/hot-date';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { MyElementsMyActivityType, ActivitySession } from './my-elements-my-activity-types';
import { findFlexibleInstanceForDate, getEffectiveSessionTimes } from './session-time-helpers';
import { MyActivityRosterings } from '../../__generated__/MyActivityRosterings';
import { DateInfoCard } from '../../../../../../common/cards/date-info-card/DateInfoCard';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { getDisplayTimeSchedule } from '../../../../../../common/functions/activity-display-helpers';
import { CapacityStatusChip } from '../../../../../../common/chips/CapacityStatusChip';
import {
  ActivityDateCapacityStatus,
  summariseCapacity,
} from '../../../../../../common/hooks/useActivityCapacityStatus';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footerWrapper: {
      display: (props: { isMobile: boolean; isMd: boolean }) => (props.isMobile || props.isMd ? 'auto' : 'flex'),
      alignItems: 'center',
      marginTop: (props: { isMobile: boolean; isMd: boolean }) => (props.isMobile ? '16px' : '40px'),
    },
    footerItem: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: '24px',
      paddingTop: (props: { isMobile: boolean; isMd: boolean }) =>
        props.isMobile ? '4px' : props.isMd ? '8px' : '0px',
    },
    footerItemIcon: {
      color: theme.color.grey.neutral200,
      fontSize: 14,
      marginRight: '6px',
    },
    footerItemLargeNumber: {
      fontWeight: 600,
      color: theme.color.grey.neutral200,
      fontSize: 14,
      marginRight: '6px',
    },
    footerItemText: {
      color: theme.color.grey.neutral200,
      marginTop: '1px',
    },
  })
);

interface Props {
  myActivity: MyElementsMyActivityType;
  onClick: () => void;
  color: {
    primary: string;
    secondary: string;
  };
  actionMenu: React.ReactNode;
  rostering?: MyActivityRosterings;
  isSelected: boolean;
  conflict?: boolean;
  capacityStatus?: ActivityDateCapacityStatus;
}

export const MyElementsMyActivityListItem = (props: Props) => {
  const {
    myActivity: {
      activityDate,
      activityName,
      startTime,
      endTime,
      activityLocation,
      numSessions,
      programName,
      sessions,
      publishedRosterName,
    },
    onClick,
    color,
    actionMenu,
    rostering,
    isSelected,
    conflict,
    capacityStatus,
  } = props;

  const { isMobile, isMd, theme } = useCampfireTheme();
  const classes = useStyles({ isMobile, isMd });
  const formatTime = (time: string) => unpackToTime(time).toFormat('h:mm a');

  const renderActivityTime = (
    activityStartTime: string,
    activityEndTime: string,
    activitySessions?: ActivitySession[],
    dateForActivity?: string
  ) => {
    // Check if user has individual shift times on their rosterings
    const sessionRosteringsWithTimes = rostering?.sessionRosterings.filter(
      (sr) => sr && ((sr as any).startTime || (sr as any).endTime)
    );

    // If any session rosterings have custom times, compute range across ALL rostered sessions
    if (sessionRosteringsWithTimes && sessionRosteringsWithTimes.length > 0) {
      let earliestStart = '';
      let latestEnd = '';

      if (rostering) {
        rostering.sessionRosterings.forEach((sr) => {
          if (!sr?.session) return;
          const srAny = sr as any;
          // Use custom times if set, otherwise look up the session's effective times for this date
          const matchedSession = activitySessions?.find((s) => s.sessionId === sr.session.sessionId);
          const sessionTimes = getEffectiveSessionTimes(matchedSession, dateForActivity);
          const srStart = srAny.startTime || sessionTimes.startTime;
          const srEnd = srAny.endTime || sessionTimes.endTime;
          if (srStart && (!earliestStart || srStart < earliestStart)) {
            earliestStart = srStart;
          }
          if (srEnd && (!latestEnd || srEnd > latestEnd)) {
            latestEnd = srEnd;
          }
        });
      }

      if (earliestStart && latestEnd) {
        return `${formatTime(earliestStart)} - ${formatTime(latestEnd)}`;
      }
      if (earliestStart) {
        return `From ${formatTime(earliestStart)}`;
      }
      if (latestEnd) {
        return `Until ${formatTime(latestEnd)}`;
      }
    }

    // Check if user is rostered for any sessions
    const rosteredSessionIds =
      rostering?.sessionRosterings.filter((sr) => sr?.session).map((sr) => sr.session.sessionId) || [];

    // Find all rostered sessions
    const rosteredSessions =
      activitySessions?.filter((session) => rosteredSessionIds.includes(session.sessionId)) || [];

    // If we have rostered sessions, calculate the latest end time
    let latestEndTime = '';
    let earliestStartTime = '';

    // First check if we have flexible sessions with an instance matching this date
    if (activitySessions && dateForActivity) {
      // Match each flexible session to its instance for this specific date (the array can hold
      // instances for multiple upcoming dates, so we can't assume index 0).
      const flexibleSessionsWithInstances = activitySessions
        .filter((session) => session.isFlexible)
        .map((session) => ({ session, instance: findFlexibleInstanceForDate(session, dateForActivity) }))
        .filter((entry) => entry.instance);

      if (flexibleSessionsWithInstances.length > 0) {
        // For rostered sessions, track the earliest start and latest end time
        if (rosteredSessions.length > 0) {
          // Get the earliest start time and latest end time from all rostered sessions
          rosteredSessions.forEach((session) => {
            const { startTime: sessionStart, endTime: sessionEnd } = getEffectiveSessionTimes(session, dateForActivity);
            if (sessionStart && (!earliestStartTime || sessionStart < earliestStartTime)) {
              earliestStartTime = sessionStart;
            }
            if (sessionEnd && (!latestEndTime || sessionEnd > latestEndTime)) {
              latestEndTime = sessionEnd;
            }
          });

          // If we have calculated valid times, return the time range
          if (earliestStartTime && latestEndTime) {
            return `${formatTime(earliestStartTime)} - ${formatTime(latestEndTime)}`;
          }
        }

        // If no rostered sessions or couldn't calculate times, show flexible session times
        const flexibleTimes = flexibleSessionsWithInstances
          .map(({ session, instance }) => {
            if (instance && instance.startTime && instance.endTime) {
              const timeStr = getDisplayTimeSchedule(instance.startTime, instance.endTime, true, instance);
              // If we have multiple sessions, prefix with session name
              const displayName = instance.name || session.name;
              return flexibleSessionsWithInstances.length > 1 ? `${displayName}: ${timeStr}` : timeStr;
            }
            return null;
          })
          .filter(Boolean);

        if (flexibleTimes.length > 0) {
          return flexibleTimes.join(', ');
        }
      }
    }

    // Default to regular activity time
    return activityStartTime && activityEndTime
      ? `${formatTime(activityStartTime)} - ${formatTime(activityEndTime)}`
      : '';
  };

  return (
    <ListItem
      onClick={onClick}
      style={{
        cursor: 'pointer',
        padding: '0',
      }}
    >
      <Box marginTop='0.9375rem' width='100%'>
        <DateInfoCard date={activityDate} color={color} selected={isSelected} size='l' conflict={conflict}>
          <Box display='flex' flexDirection='column' width='100%'>
            <Box display='flex' justifyContent='space-between' alignSelf='stretch'>
              <TitularTooltip title={rostering?.publishedRoster?.name || publishedRosterName || activityName}>
                <Box>
                  <Typography
                    variant={'h6'}
                    noWrap={!isMobile}
                    style={
                      isMobile
                        ? {
                            color: theme.color.grey.neutral400,
                            fontWeight: 800,
                            width: '100%',
                            letterSpacing: '-0.02em',
                            overflow: 'hidden',
                            maxHeight: '3.5em',
                            textOverflow: 'ellipsis',
                          }
                        : {
                            color: theme.color.grey.neutral400,
                            fontWeight: 800,
                            width: '100%',
                            letterSpacing: '-0.02em',
                          }
                    }
                  >
                    {rostering?.publishedRoster?.name || publishedRosterName || activityName}
                  </Typography>
                  {(rostering?.publishedRoster?.name || publishedRosterName) && (
                    <Typography variant='body2' style={{ color: theme.color.grey.neutral300, fontWeight: 500 }}>
                      {activityName}
                    </Typography>
                  )}
                </Box>
              </TitularTooltip>
              <Box display='flex' flexDirection='column' alignItems='stretch'>
                <Box
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    backgroundColor: theme.color.white.neutral50,
                    borderRadius: 48,
                  }}
                >
                  {actionMenu}
                </Box>
                {capacityStatus?.overallStatus && (
                  <CapacityStatusChip
                    status={capacityStatus.overallStatus}
                    {...summariseCapacity(capacityStatus.sessions)}
                    size='small'
                  />
                )}
              </Box>
            </Box>
            {rostering ? (
              <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
                Attending:{' '}
                {rostering.sessionRosterings
                  .filter((sessionRostering) => sessionRostering?.session)
                  .map((sessionRostering) => {
                    let sessionName = sessionRostering.session.name;

                    // Check if there's a matching session with flexible instances
                    if (sessions && sessions.length > 0) {
                      const { sessionId } = sessionRostering.session;
                      const matchingSession = sessions.find((session) => session.sessionId === sessionId);

                      // If we have a matching session with flexible instances
                      if (
                        matchingSession &&
                        matchingSession.isFlexible &&
                        matchingSession.currentFlexibleInstances &&
                        matchingSession.currentFlexibleInstances.length > 0
                      ) {
                        // Get the first flexible instance (which should be for the current date)
                        const flexibleInstance = matchingSession.currentFlexibleInstances[0];

                        // Use the instance name if available, or fall back to the session name
                        if (flexibleInstance.name) {
                          sessionName = flexibleInstance.name;
                        }
                      }
                    }

                    // Add individual shift times if set
                    const sr = sessionRostering as any;
                    if (sr.startTime || sr.endTime) {
                      const startStr = sr.startTime ? formatTime(sr.startTime) : '';
                      const endStr = sr.endTime ? formatTime(sr.endTime) : '';
                      if (startStr && endStr) {
                        return `${sessionName} (${startStr} - ${endStr})`;
                      }
                      if (startStr) {
                        return `${sessionName} (from ${startStr})`;
                      }
                      if (endStr) {
                        return `${sessionName} (until ${endStr})`;
                      }
                    }

                    return sessionName;
                  })
                  .join(', ')}
              </Typography>
            ) : null}
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
              {renderActivityTime(startTime, endTime, sessions, activityDate)}
            </Typography>
            <Box className={classes.footerWrapper}>
              <Box className={classes.footerItem}>
                <LocationOnRounded className={classes.footerItemIcon} />
                <Typography variant='caption' className={classes.footerItemText}>
                  {activityLocation}
                </Typography>
              </Box>
              {/* <Box className={classes.footerItem}>
              <AccessTime className={classes.footerItemIcon} />
              <Typography variant='caption' className={classes.footerItemText}>
                {hasOpenRoster ? 'Open Roster' : 'Managed Roster'}
              </Typography>
            </Box> */}
              <Box className={classes.footerItem}>
                <Typography variant='h2' className={classes.footerItemLargeNumber}>
                  {numSessions}
                </Typography>
                <Typography variant='caption' className={classes.footerItemText}>
                  {numSessions > 1 ? 'sessions available' : 'session available'}
                </Typography>
              </Box>
              <Box className={classes.footerItem}>
                <Box display='flex' alignItems='center' pt={1}>
                  <Explore style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
                  <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
                    {programName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </DateInfoCard>
      </Box>
    </ListItem>
  );
};
